/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, SelectField, SelectOption, Typography2 } from '@coursera/cds-core';

import { EDUCATION_LEVEL_OPTIONS } from 'bundles/onboarding-common/constants';
import type { EDUCATION_LEVEL_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import useLearnerObjectUpdateEducationProfile from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateEducationProfile';
import { getEducationLevelOptionName } from 'bundles/onboarding-common/utils';

import _t from 'i18n!nls/personalization-settings';

// CDS SelectField's `fullWidth` only widens the outer FormControl, not the
// input box itself (SelectField.tsx never forwards `fullWidth` to the inner
// Select), leaving the visible control shrunk to its content width instead of
// filling the row. Force it via CDS's own public class hook. (SelectField2
// forwards fullWidth correctly, but it's a differently-shaped react-aria API
// with no usage precedent in this codebase - not a drop-in swap for this ticket.)
const styles = {
  select: css`
    margin-top: var(--cds-spacing-50);

    .cds-select-field-root {
      width: 100%;
    }
  `,
};

const EDUCATION_LEVEL_VALUES: readonly string[] = Object.values(EDUCATION_LEVEL_OPTIONS);

const isEducationLevel = (value: unknown): value is EDUCATION_LEVEL_OPTIONS_TYPE =>
  typeof value === 'string' && EDUCATION_LEVEL_VALUES.includes(value);

export type EducationLevelSettingItemProps = {
  label: string;
  /** The saved value in onboarding-option shape, from the page-level query. */
  savedValue: EDUCATION_LEVEL_OPTIONS_TYPE | null;
};

const EducationLevelSettingItem = ({ label, savedValue }: EducationLevelSettingItemProps) => {
  const { updateEducationProfile } = useLearnerObjectUpdateEducationProfile();
  const optionNames = getEducationLevelOptionName();

  // The mutation doesn't update the read query's cache, so the Select's value
  // is tracked in local state (seeded from the saved value once loaded) rather
  // than bound directly to the query result, which would never change after a
  // selection.
  const [selectedValue, setSelectedValue] = React.useState<EDUCATION_LEVEL_OPTIONS_TYPE | null>(savedValue);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target;
    if (!isEducationLevel(value)) return;
    setSelectedValue(value);
    updateEducationProfile(value);
  };

  return (
    <Grid container direction="column">
      <Typography2 component="p" variant="subtitleMedium">
        {label}
      </Typography2>
      <SelectField
        css={styles.select}
        aria-label={label}
        optional
        placeholder={_t('Select an option')}
        value={selectedValue ?? ''}
        onChange={handleChange}
        fullWidth
      >
        {Object.values(EDUCATION_LEVEL_OPTIONS).map((value) => (
          <SelectOption key={value} value={value}>
            {optionNames[value]}
          </SelectOption>
        ))}
      </SelectField>
    </Grid>
  );
};

export default EducationLevelSettingItem;
